import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StoreContext = createContext(null);

export const useStore = () => {
  const context = useContext(StoreContext);
  if (!context) {
    throw new Error('useStore must be used within StoreProvider');
  }
  return context;
};

export const StoreProvider = ({ children }) => {
  const [stores, setStores] = useState([]);
  const [currentStore, setCurrentStore] = useState(null);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const fetchStores = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/stores`, {
        withCredentials: true
      });
      setStores(response.data);
      if (response.data.length > 0 && !currentStore) {
        // Auto-select MAGERE store for standalone mode, fallback to saved or first
        const savedStoreId = localStorage.getItem('currentStoreId');
        const savedStore = savedStoreId ? response.data.find(s => s.id === savedStoreId) : null;
        const magereStore = response.data.find(s => s.slug === 'magere-viandes');
        setCurrentStore(savedStore || magereStore || response.data[0]);
      }
    } catch (error) {
      console.error('Failed to fetch stores:', error);
    } finally {
      setLoading(false);
    }
  }, [currentStore]);

  // Re-fetch stores when user changes (login/logout)
  useEffect(() => {
    if (user) {
      fetchStores();
    } else {
      setStores([]);
      setCurrentStore(null);
    }
  }, [user]);

  const createStore = async (data) => {
    const response = await axios.post(`${API_URL}/api/stores`, data, {
      withCredentials: true
    });
    await fetchStores();
    return response.data;
  };

  const updateStore = async (storeId, data) => {
    const response = await axios.put(`${API_URL}/api/stores/${storeId}`, data, {
      withCredentials: true
    });
    await fetchStores();
    return response.data;
  };

  const updateStoreSettings = async (storeId, settings) => {
    const response = await axios.put(`${API_URL}/api/stores/${storeId}/settings`, settings, {
      withCredentials: true
    });
    return response.data;
  };

  const publishStore = async (storeId) => {
    const response = await axios.post(`${API_URL}/api/stores/${storeId}/publish`, {}, {
      withCredentials: true
    });
    await fetchStores();
    return response.data;
  };

  const selectStore = (store) => {
    setCurrentStore(store);
    localStorage.setItem('currentStoreId', store.id);
  };

  const value = {
    stores,
    currentStore,
    loading,
    fetchStores,
    createStore,
    updateStore,
    updateStoreSettings,
    publishStore,
    selectStore
  };

  return (
    <StoreContext.Provider value={value}>
      {children}
    </StoreContext.Provider>
  );
};

export default StoreContext;
